import Grid from '@mui/material/Grid2'
import Typography from '@mui/material/Typography'
import { QueryClient, useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { QUERY_PARKING_EVENT_DETAILS } from '../../../apis/moovyQueryKeys'
import {
  ParkingEventDetailsDTO,
  UpdatePriceDTO,
} from '../../../apis/parkingEvents/dto'
import { ParkingEventsApi } from '../../../apis/parkingEvents/parkingEventsApi'
import { hasPermission } from '../../../app/permissions'
import testidKeys from '../../../app/testidKeys'
import {
  MoovyLink,
  MoovyRestricted,
  MoovyScrollToTop,
  PrettyCurrency,
  ValueOrElse,
} from '../../../components'
import useAuth from '../../../hooks/useAuth'
import {
  prettyDuration,
  prettyFullTimestamp,
} from '../../../utils/datetimeUtils'
import { MoovyToast } from '../../../utils/moovyToast'
import { PaymentStatusIconWithText } from '.././paymentStatusIcon'
import { DividerRow, RowLabel, RowValue } from './detailsGridComponents'
import ModalEditFee from './modalEditFee'

interface ParkingDetailsSummaryProps {
  parkingDetailsRef: string
  parkingDetails?: ParkingEventDetailsDTO
  queryClient: QueryClient
}

const ParkingDetailsSummary = (props: ParkingDetailsSummaryProps) => {
  const { parkingDetails, parkingDetailsRef, queryClient } = props
  const intl = useIntl()
  const { isAdmin } = useAuth()

  const [showEditFeeModal, setShowEditFeeModal] = useState(false)

  // Do not render when parkingDetails is not (yet) available
  if (!parkingDetails) {
    return null
  }

  const updatePriceMutation = useMutation({
    mutationFn: (updateRequest: UpdatePriceDTO) =>
      ParkingEventsApi.updatePrice({
        id: parkingDetailsRef,
        values: updateRequest,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_PARKING_EVENT_DETAILS] })
      MoovyToast.showToast(
        <FormattedMessage id="page.parkingDetails.editFee.success" />,
        'success',
      )
      setShowEditFeeModal(false)
    },
  })

  const onCancel = () => {
    updatePriceMutation.reset()
    setShowEditFeeModal(false)
  }

  return (
    <>
      <MoovyScrollToTop />
      <Grid container direction="row" spacing={1} paddingTop={2}>
        {/* Display details */}
        <RowLabel formattedMessageId="page.parkingDetails.id" />
        <RowValue>
          <Typography>{parkingDetailsRef}</Typography>
        </RowValue>
        <DividerRow />

        <RowLabel formattedMessageId="common.text.licensePlate" />
        <RowValue>
          <Typography>
            <ValueOrElse>{parkingDetails.licensePlate}</ValueOrElse>
          </Typography>
        </RowValue>
        <DividerRow />

        <RowLabel formattedMessageId="common.text.exempt" />
        <RowValue>
          <Typography>
            <FormattedMessage
              id={
                parkingDetails.parkingType === 'WHITELIST'
                  ? 'common.yes'
                  : 'common.no'
              }
            />
          </Typography>
        </RowValue>
        <DividerRow />

        <RowLabel formattedMessageId="common.text.hasEnded" />
        <RowValue>
          <Typography>
            <FormattedMessage
              id={parkingDetails.endTime === null ? 'common.no' : 'common.yes'}
            />
          </Typography>
        </RowValue>
        <DividerRow />

        <RowLabel formattedMessageId="common.text.duration" />
        <RowValue>
          <Typography>{prettyDuration(intl, parkingDetails)}</Typography>
        </RowValue>
        <DividerRow />

        <RowLabel formattedMessageId="common.text.startTime" />
        <RowValue>
          <Typography>
            <ValueOrElse>
              {prettyFullTimestamp(intl, parkingDetails.startTime)}
            </ValueOrElse>
          </Typography>
        </RowValue>
        <DividerRow />

        <RowLabel formattedMessageId="common.text.endTime" />
        <RowValue>
          <Typography>
            <ValueOrElse>
              {prettyFullTimestamp(intl, parkingDetails.endTime)}
            </ValueOrElse>
          </Typography>
        </RowValue>
        <DividerRow />

        <RowLabel formattedMessageId="common.text.entryLane" />
        <RowValue>
          <Typography>
            <ValueOrElse>{parkingDetails.lane}</ValueOrElse>
          </Typography>
        </RowValue>
        <DividerRow />

        <RowLabel formattedMessageId="common.text.exitLane" />
        <RowValue>
          <Typography>
            <ValueOrElse>{parkingDetails.exitCamera?.lane}</ValueOrElse>
          </Typography>
        </RowValue>
        <DividerRow />

        <RowLabel formattedMessageId="common.text.price" />
        <RowValue>
          <ValueOrElse>
            <PrettyCurrency cents={parkingDetails.price} />
          </ValueOrElse>
          {
            /* Only render the edit button if all of the following conditions are met:
             * - The user has EDIT permission for PARKING_EVENTS
             * - The parking event has ended and has not been paid or invalidated yet, i.e. the payment status is UNPAID
             * - The parking event was not created by an exempt vehicle */
            hasPermission(isAdmin, 'EDIT', 'PARKING_EVENTS') &&
              parkingDetails.paymentStatus === 'UNPAID' &&
              parkingDetails.parkingType !== 'WHITELIST' && (
                <MoovyLink.LinkButton
                  sx={{
                    marginLeft: 1,
                    color: 'error.main',
                  }}
                  data-testid={testidKeys.SUMMARY_EDIT_FEE_BUTTON}
                  aria-label={intl.formatMessage({
                    id: 'page.parkingDetails.editFee',
                  })}
                  onClick={() => setShowEditFeeModal(true)}
                >
                  <FormattedMessage id="page.parkingDetails.editFee" />
                </MoovyLink.LinkButton>
              )
          }
        </RowValue>
        <DividerRow />

        <RowLabel formattedMessageId="common.text.paymentStatus" />
        <RowValue>
          <PaymentStatusIconWithText
            paymentStatus={parkingDetails.paymentStatus}
          />
        </RowValue>
        <DividerRow />
      </Grid>
      <MoovyRestricted action="EDIT" resource="PARKING_EVENTS">
        <ModalEditFee
          isOpen={showEditFeeModal}
          parkingDetails={parkingDetails}
          mutation={updatePriceMutation}
          errorTitleKey="page.parkingDetails.editFee.genericError"
          onCancel={onCancel}
          onSubmit={(values) => updatePriceMutation.mutate(values)}
          invalidationOnly={true}
        />
      </MoovyRestricted>
    </>
  )
}

export default ParkingDetailsSummary
